import React, { useRef } from 'react';
import './InvoicePrinter.css';
import logo from './../Logos/Priballl.png';
import { formatQuoteValidUntilDisplay, getQuoteLinePrices, getQuoteProductDescription, getQuoteProductImageUrl } from '../utils/quoteDocument';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  
  // Manejar diferentes formatos de fecha
  let dateObj;
  if (date.seconds) {
    // Timestamp de Firestore
    dateObj = new Date(date.seconds * 1000);
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'N/A';
  }
  
  return dateObj.toLocaleString('es-AR');
};

// Formatear vencimiento CAE (AFIP devuelve YYYYMMDD)
const formatVencimientoCAE = (vencimiento_cae) => {
  if (!vencimiento_cae) return 'N/A';
  const s = String(vencimiento_cae).replace(/\D/g, '');
  if (s.length !== 8) return vencimiento_cae;
  const y = s.slice(0, 4), m = s.slice(4, 6), d = s.slice(6, 8);
  return `${d}/${m}/${y}`;
};

const getTipoComprobanteLabel = (tipo) => {
  const map = { 11: 'Factura C', 6: 'Factura B', 1: 'Factura A' };
  return map[Number(tipo)] || `Tipo ${tipo}`;
};

const formatCuitDisplay = (cuit) => {
  const s = String(cuit || '').replace(/\D/g, '');
  if (s.length !== 11) return s || '';
  return `${s.slice(0, 2)}-${s.slice(2, 10)}-${s.slice(10)}`;
};

const FECHA_INICIO_ACTIVIDADES = '01/11/2012';
const INGRESOS_BRUTOS = '280-404387';
const CUIT_EMISOR_DEFAULT = '20-33602790-0';

const INVOICE_PRINT_CSS = `
*{box-sizing:border-box}
html,body{margin:0;padding:0;background:#fff;color:#111;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif}
.invoice-content{background:#fff;color:#111;padding:12px;display:block;overflow:visible;height:auto;min-height:0;width:100%}
.invoice-sep{border:none;border-top:1px solid #000;margin:10px 0}
.invoice-sticky-header,.invoice-sticky-footer{position:static}
.invoice-original{text-align:center;font-size:11px;margin:0 0 8px;font-weight:500}
.invoice-header-three-cols{display:grid;grid-template-columns:1fr auto 1fr;gap:12px;font-size:11px;align-items:start}
.invoice-col-emisor{min-width:0;padding:6px 0}
.invoice-col-emisor p{margin:2px 0;line-height:1.35}
.invoice-logo-small{max-width:72px;max-height:36px;width:auto;height:auto;display:block;margin-bottom:6px;object-fit:contain}
.invoice-col-tipo{display:flex;align-items:center;justify-content:center}
.tipo-box{background:#e0e0e0;color:#000;width:64px;min-height:72px;padding:8px 4px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;border:1px solid #bbb;border-radius:6px}
.tipo-letra{font-size:28px;font-weight:700;line-height:1}
.tipo-codigo{font-size:11px;margin-top:4px;font-weight:600}
.tipo-monotributista{font-size:9px;margin-top:2px}
.invoice-col-datos{min-width:0}
.invoice-col-datos p{margin:2px 0}
.invoice-factura-title{font-size:14px;font-weight:700;margin:0 0 6px}
.invoice-body{display:block;overflow:visible}
.invoice-receptor-block{display:grid;grid-template-columns:1fr 1fr;gap:12px;font-size:11px}
.invoice-receptor-block p{margin:2px 0}
.invoice-condiciones-venta{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;font-size:11px}
.invoice-condiciones-venta p{margin:0}
.afip-table-wrap{overflow:visible}
.products-table{width:100%;border-collapse:collapse;font-size:11px}
.products-table th,.products-table td{border:1px solid #000;padding:5px 6px;vertical-align:top}
.products-table thead th{background:#b8d4e8;font-weight:700}
.products-table .qty,.products-table .price,.products-table .total{text-align:right}
.price-discounted{font-weight:700;color:#15803d}
.quote-thumb-cell{width:58px;text-align:center}
.quote-thumb{width:48px;height:48px;object-fit:contain;display:block;margin:0 auto}
.quote-item-name{display:block}
.quote-item-desc{display:block;margin-top:4px;font-size:9px;font-weight:400;line-height:1.3;color:#444}
.invoice-totales-afip{padding:10px 0;font-size:11px}
.totales-resumen{margin-left:auto;max-width:280px}
.totales-resumen p{display:flex;justify-content:space-between;margin:4px 0;gap:12px}
.totales-total-row{font-size:13px;font-weight:700}
.invoice-quote-notes{margin:8px 0 0;font-size:11px}
.invoice-footer-afip{display:flex;justify-content:space-between;gap:16px;margin-top:16px;min-height:80px;font-size:11px}
.footer-qr-img{width:72px;height:72px;border:1px solid #000}
.footer-comprobante-autorizado{font-weight:700;margin:4px 0}
.footer-leyenda{margin:0;max-width:280px}
.footer-right{text-align:right}
.footer-pagina{font-size:10px;margin-top:8px}
.testing-banner,.testing-footer{background:#ff6b6b;color:#fff;text-align:center;padding:8px;font-weight:700}
@media print{
  @page{size:A4;margin:10mm}
  html,body{background:#fff}
  .invoice-content{padding:0}
}
`;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const srcToDataUrl = async (src) => {
  if (!src || src.startsWith('data:')) return src;
  try {
    const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return src;
    return await blobToDataUrl(await res.blob());
  } catch {
    try {
      return await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch {
            resolve(src);
          }
        };
        img.onerror = () => resolve(src);
        img.src = src;
      });
    } catch {
      return src;
    }
  }
};

const prepareInvoiceHtmlForPrint = async (invoiceEl) => {
  const clone = invoiceEl.cloneNode(true);
  const images = Array.from(clone.querySelectorAll('img'));
  await Promise.all(images.map(async (img) => {
    const src = img.getAttribute('src') || '';
    if (!src) return;
    const next = await srcToDataUrl(src);
    img.setAttribute('src', next);
    img.removeAttribute('srcset');
  }));
  return clone.outerHTML;
};

const InvoicePrinter = ({ 
  saleData, 
  invoiceData = null,
  creditNoteData = null,
  showPrintButton = true,
  showWindowClose = false,
  compact = false,
  fullPage = false,
  title = "Comprobante de Venta"
}) => {
  const printRef = useRef();

  // Verificar que tengamos datos mínimos
  if (!saleData) {
    return (
      <div className="invoice-error">
        <p>⚠️ No hay información de venta disponible</p>
      </div>
    );
  }

  const isQuote = invoiceData?.isQuote === true || saleData?.isQuote === true;
  const isDraftQuote = isQuote && saleData?.isDraft === true;
  const printTitle = isDraftQuote ? 'Vista previa del presupuesto' : (isQuote ? 'Presupuesto Priball' : title);

  const handlePrint = async () => {
    const invoiceEl = printRef.current;
    if (!invoiceEl) return;

    let html;
    try {
      html = await prepareInvoiceHtmlForPrint(invoiceEl);
    } catch (error) {
      console.error('No se pudo preparar el presupuesto para imprimir:', error);
      html = invoiceEl.outerHTML;
    }

    const win = window.open('', '_blank');
    if (!win) {
      console.error('El navegador bloqueó la ventana de impresión. Permití ventanas emergentes.');
      return;
    }

    win.document.open();
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${printTitle}</title><style>${INVOICE_PRINT_CSS}</style></head><body>${html}</body></html>`);
    win.document.close();

    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      try {
        win.focus();
        win.print();
      } catch (error) {
        console.error('Error al abrir el diálogo de impresión:', error);
      }
    };

    const waitThenPrint = () => {
      if (win.document.readyState !== 'complete') {
        win.onload = waitThenPrint;
        return;
      }
      const imgs = Array.from(win.document.querySelectorAll('img'));
      if (imgs.every((img) => img.complete)) {
        window.setTimeout(doPrint, 250);
        return;
      }
      let waited = 0;
      const check = () => {
        if (imgs.every((img) => img.complete) || waited >= 4000) {
          window.setTimeout(doPrint, 250);
          return;
        }
        waited += 200;
        window.setTimeout(check, 200);
      };
      window.setTimeout(check, 200);
    };

    win.addEventListener('afterprint', () => {
      window.setTimeout(() => {
        try { win.close(); } catch (_) { /* ignore */ }
      }, 200);
    });
    win.onload = waitThenPrint;
    window.setTimeout(waitThenPrint, 500);
  };

  // Calcular totales usando la nueva estructura
  const calculateTotals = () => {
    if (!saleData.products?.length) return { subtotal: 0, discountAmount: 0, total: 0, discountType: null, discountValue: null };
    
    const subtotal = saleData.subtotal || 
      saleData.products.reduce((total, product) => total + (product.price * product.quantity), 0);
    
    const discountAmount = saleData.discountAmount || 0;
    const total = saleData.totalPrice || (subtotal - discountAmount);
    const discountType = saleData.discountType || null;
    const discountValue = saleData.discount != null ? saleData.discount : null;
    
    return { subtotal, discountAmount, total, discountType, discountValue };
  };

  // Obtener medios de pago seleccionados
  const getPaymentMethods = () => {
    if (isQuote) {
      if (!saleData.paymentMethods) return 'A convenir';
      const listed = Object.entries(saleData.paymentMethods)
        .filter(([, value]) => value)
        .map(([key]) => (key === 'consignacion' ? 'Consignación' : key.charAt(0).toUpperCase() + key.slice(1)))
        .join(', ');
      return listed || 'A convenir';
    }
    if (!saleData.paymentMethods) return 'No especificado';
    
    return Object.entries(saleData.paymentMethods)
      .filter(([, value]) => value)
      .map(([key]) => (key === 'consignacion' ? 'Consignación' : key.charAt(0).toUpperCase() + key.slice(1)))
      .join(', ') || 'No especificado';
  };

  // Determinar si es testing
  const isTesting = invoiceData?.is_testing || saleData.is_testing || false;

  const { subtotal, discountAmount, total, discountType, discountValue } = calculateTotals();
  const hasDiscount = discountAmount > 0;
  const discountLabel = hasDiscount && discountType === 'percentage' && discountValue != null
    ? `Descuento (${formatNumber(discountValue)}%):`
    : hasDiscount && discountType === 'fixed' && discountValue != null
      ? 'Descuento (monto fijo):'
      : hasDiscount
        ? 'Descuento:'
        : '';

  const codTipo = isQuote ? 'PRE' : String(invoiceData?.tipo_comprobante ?? 11).padStart(3, '0');
  const fechaEmision = invoiceData?.fecha_emision
    ? (() => {
        const s = String(invoiceData.fecha_emision).replace(/\D/g, '');
        if (s.length === 8) return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
        return 'N/A';
      })()
    : formatDate(saleData.date);
  const cuitEmisor = invoiceData?.debug?.cuit ? formatCuitDisplay(invoiceData.debug.cuit) : CUIT_EMISOR_DEFAULT;
  const receptorNombre = (saleData.customerData?.name || 'CONSUMIDOR FINAL').toUpperCase();
  const receptorDoc = saleData.customerData?.cuit ? formatCuitDisplay(saleData.customerData.cuit) : '00-00000000-0';
  /* Domicilio, ciudad, provincia, país, código postal y notas son solo gestión interna: no se imprimen en la factura */
  const receptorDomicilio = 'Sin Domicilio';
  const pv = String(invoiceData?.punto_venta ?? 0).padStart(5, '0');
  const cbteNro = String(invoiceData?.numero_comprobante ?? 0).padStart(8, '0');
  const quoteCurrency = isQuote
    ? (invoiceData?.currency || saleData?.currency || 'Pesos Argentinos')
    : 'Pesos Argentinos';
  const quoteDestination = isQuote
    ? (invoiceData?.destinationProvince || saleData?.destinationProvince || 'Córdoba')
    : 'Córdoba';
  const quotePaymentTerms = isQuote
    ? (String(invoiceData?.paymentTerms || saleData?.paymentTerms || '').trim() || getPaymentMethods())
    : getPaymentMethods();
  const validUntilDisplay = formatQuoteValidUntilDisplay(invoiceData?.validUntil || saleData?.validUntil);
  const showQuoteImages = isQuote && (invoiceData?.includeImages === true || saleData?.includeImages === true);
  const showQuoteDesc = isQuote && (invoiceData?.includeDescription === true || saleData?.includeDescription === true);

  return (
    <div className={`invoice-printer-wrapper ${fullPage ? 'is-full-page' : ''}`}>
      <div ref={printRef} className={`invoice-content afip-layout ${compact ? 'compact' : ''} ${isTesting ? 'testing-mode' : ''}`}>
        
        {isTesting && (
          <div className="testing-banner">
            <span>🧪 MODO TESTING - SIN VALIDEZ FISCAL</span>
          </div>
        )}

        {/* Encabezado — como imagen 1: logo + datos | caja C gris clara | FACTURA DE VENTA */}
        <header className="invoice-sticky-header">
          <p className="invoice-original">ORIGINAL</p>
          <div className="invoice-header-three-cols">
            <div className="invoice-col-emisor">
              <img src={logo} alt="Priball" className="invoice-logo-small" />
              <p><strong>Razón Social:</strong> Priball - Balones Deportivos</p>
              <p><strong>Domicilio:</strong> Ruta 9 y Junín, Bell Ville, Cba.</p>
              <p>Tel: (3537) 302888 · www.priball.com</p>
              <p>Monotributista · CUIT: 20-33602790-0</p>
              <p><strong>Ingresos Brutos:</strong> 280-404387</p>
              <p><strong>Fecha de inicio de Actividades:</strong> 01/11/2012</p>
              <p><strong>Punto de venta:</strong> {String(invoiceData?.punto_venta ?? 8).padStart(4, '0')}</p>
            </div>
            <div className="invoice-col-tipo">
              <div className="tipo-box">
                <span className="tipo-letra">{isQuote ? 'P' : 'C'}</span>
                <span className="tipo-codigo">{codTipo}</span>
                <span className="tipo-monotributista">Monotributista</span>
              </div>
            </div>
            <div className="invoice-col-datos">
              <p className="invoice-factura-title">{isQuote ? 'PRESUPUESTO' : 'FACTURA DE VENTA'}</p>
              <p><span>Número:</span> <strong>{isDraftQuote ? 'Vista previa' : `${isQuote ? 'PRE' : 'C'}-${pv}-${cbteNro}`}</strong></p>
              <p><span>Fecha:</span> <strong>{fechaEmision}</strong></p>
              <p><span>CUIT:</span> <strong>{cuitEmisor}</strong></p>
              <p><span>Ing. Brutos:</span> <strong>{INGRESOS_BRUTOS}</strong></p>
              <p><span>Inicio Act.:</span> <strong>{FECHA_INICIO_ACTIVIDADES}</strong></p>
            </div>
          </div>
        </header>
        <hr className="invoice-sep" />

        <main className="invoice-body">
        {/* Receptor — dos columnas, como imagen 1 */}
        <div className="invoice-receptor-block">
          <div className="receptor-left">
            <p><span>Sr. (es):</span> <strong>{receptorNombre}</strong></p>
            <p><span>Domicilio:</span> {receptorDomicilio}</p>
          </div>
          <div className="receptor-right">
            <p><span>CUIT:</span> <strong>{receptorDoc}</strong></p>
            <p><span>Cond. IVA:</span> Consumidor Final</p>
          </div>
        </div>
        <hr className="invoice-sep" />

        {/* Moneda, Prov. Destino, Fecha Vto., Forma de pago — una fila compacta */}
        <div className="invoice-condiciones-venta">
          <p><span>Moneda:</span> {quoteCurrency}</p>
          <p><span>Prov. Destino:</span> {quoteDestination}</p>
          <p><span>{isQuote ? 'Válido hasta:' : 'Fecha Vto.:'}</span> <strong>{isQuote ? (validUntilDisplay || fechaEmision) : fechaEmision}</strong></p>
          <p><span>Forma de pago:</span> {quotePaymentTerms}</p>
        </div>
        <hr className="invoice-sep" />

        {/* Tabla — encabezado celeste, bordes finos negros como imagen 1 */}
        <div className="products-section afip-table-wrap">
          <table className="products-table afip-table">
            <thead>
              <tr>
                {showQuoteImages ? <th className="quote-thumb-cell">Foto</th> : (isQuote ? null : <th>Cód.</th>)}
                <th>Artículo</th>
                {isQuote ? null : <th>Observaciones</th>}
                <th>Cantidad</th>
                <th>{isQuote ? 'P. lista' : 'Precio'}</th>
                {isQuote && hasDiscount ? <th>P. c/dto</th> : null}
                {isQuote ? null : <th>% Dto.</th>}
                <th>{isQuote && hasDiscount ? 'Total c/dto' : 'Importe'}</th>
              </tr>
            </thead>
            <tbody>
              {saleData.products?.map((product, index) => {
                const pctDto = 0;
                const imageUrl = showQuoteImages ? getQuoteProductImageUrl(product) : '';
                const itemDesc = showQuoteDesc ? getQuoteProductDescription(product) : '';
                const linePrices = isQuote ? getQuoteLinePrices(product, saleData) : null;
                const itemTotal = linePrices
                  ? linePrices.lineDiscounted
                  : (product.price || 0) * (product.quantity || 0);
                return (
                  <tr key={index}>
                    {showQuoteImages ? (
                      <td className="quote-thumb-cell">
                        {imageUrl ? (
                          <img src={imageUrl} alt="" className="quote-thumb" />
                        ) : null}
                      </td>
                    ) : (isQuote ? null : (
                      <td></td>
                    ))}
                    <td className="description">
                      <span className="quote-item-name">{product.model || product.title || 'Producto'}</span>
                      {itemDesc ? <span className="quote-item-desc">{itemDesc}</span> : null}
                    </td>
                    {isQuote ? null : <td></td>}
                    <td className="qty">{(product.quantity || 0).toFixed(2)}</td>
                    <td className="price">{formatNumber(linePrices ? linePrices.listPrice : (product.price || 0))}</td>
                    {isQuote && hasDiscount ? (
                      <td className="price price-discounted">{formatNumber(linePrices.discountedUnit)}</td>
                    ) : null}
                    {isQuote ? null : <td className="pct-dto">{pctDto.toFixed(2)}</td>}
                    <td className="total">{formatNumber(itemTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <hr className="invoice-sep" />

        {/* Totales: Subtotal, Descuento (tipo y valor), Total */}
        <div className="invoice-totales-afip">
          <div className="totales-resumen">
            {hasDiscount && (
              <>
                <p className="totales-subtotal-row"><span>Subtotal:</span> <strong>{formatNumber(subtotal)}</strong></p>
                <p className="totales-descuento-row"><span>{discountLabel}</span> <strong>-{formatNumber(discountAmount)}</strong></p>
              </>
            )}
            <p className="totales-bruto-row"><span>Bruto:</span> <strong>{formatNumber(total)}</strong></p>
            <p className="totales-impuestos-row"><span>Impuestos:</span> <strong>0.00</strong></p>
            <p className="totales-total-row"><span>Total:</span> <strong>$ {formatNumber(total)}</strong></p>
          </div>
        </div>
        {isQuote && saleData.notes ? (
          <p className="invoice-quote-notes"><span>Notas:</span> {saleData.notes}</p>
        ) : null}

        </main>

        <hr className="invoice-sep" />
        {/* Pie — como imagen 2: QR + Comprobante Autorizado | CAE, Fecha Vto. CAE, Página 1 */}
        <footer className="invoice-sticky-footer">
          <div className="invoice-footer-afip">
            <div className="footer-left">
              {invoiceData?.qr_data && (
                <div className="footer-qr">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(invoiceData.qr_data)}`}
                    alt="QR AFIP"
                    className="footer-qr-img"
                  />
                </div>
              )}
              <p className="footer-comprobante-autorizado">
                {isQuote ? 'Documento no válido como factura' : 'Comprobante Autorizado'}
              </p>
              <p className="footer-leyenda">
                {isQuote
                  ? 'Presupuesto orientativo. Precios finales, IVA incluido. No tiene validez fiscal ni CAE de AFIP.'
                  : 'Esta Agencia no se responsabiliza por los datos ingresados en el detalle de la operación.'}
              </p>
            </div>
            <div className="footer-right">
              {isQuote && validUntilDisplay ? (
                <p><span>Válido hasta:</span> {validUntilDisplay}</p>
              ) : null}
              {invoiceData?.cae && (
                <>
                  <p><span>CAE:</span> {invoiceData.cae}</p>
                  <p><span>Fecha Vto. CAE:</span> {formatVencimientoCAE(invoiceData.vencimiento_cae)}</p>
                </>
              )}
              <p className="footer-pagina">Página 1</p>
            </div>
          </div>
        </footer>

        {creditNoteData?.cae && (
          <>
            <hr className="invoice-sep" />
            <footer className="invoice-sticky-footer">
              <p className="invoice-factura-title" style={{ marginBottom: 8 }}>Nota de crédito C</p>
              <div className="invoice-footer-afip">
                <div className="footer-left">
                  {creditNoteData?.qr_data && (
                    <div className="footer-qr">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(creditNoteData.qr_data)}`}
                        alt="QR NC AFIP"
                        className="footer-qr-img"
                      />
                    </div>
                  )}
                  <p className="footer-comprobante-autorizado">NC — Comprobante Autorizado</p>
                </div>
                <div className="footer-right">
                  <p><span>NC Nº:</span> {creditNoteData.numero}</p>
                  {creditNoteData.comprobante_asociado?.numero && (
                    <p><span>Factura asociada:</span> {creditNoteData.comprobante_asociado.numero}</p>
                  )}
                  <p><span>CAE:</span> {creditNoteData.cae}</p>
                  <p><span>Vto. CAE:</span> {formatVencimientoCAE(creditNoteData.vencimiento_cae)}</p>
                </div>
              </div>
            </footer>
          </>
        )}

        {isTesting && (
          <div className="testing-footer">
            <p><strong>⚠️ COMPROBANTE DE TESTING</strong></p>
            <p>Este documento no tiene validez fiscal</p>
          </div>
        )}
      </div>

      {/* Print Button */}
      {showPrintButton && (
        <div className="print-controls">
          <button className="print-btn" onClick={handlePrint}>
            {isQuote ? (isDraftQuote ? 'Descargar / imprimir vista previa' : 'Descargar / imprimir presupuesto') : `Imprimir ${title}`}
          </button>
          {showWindowClose ? (
            <button className="print-btn secondary" onClick={() => window.close()}>
              ✕ Cerrar
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default InvoicePrinter;